import HttpCotinService from './HttpCotinService';

export interface ConfiguracoesSistemaDto {
    id: number;
    nome?: string;
    descricao?: string;
    logging?: boolean | null;
    lgpd?: boolean | null;
    brasaoOficial?: boolean | null;
    manutencao?: boolean | null;
    manutencaoDescricao?: string;
    manutencaoDataInicio?: string | null;
    manutencaoDataFim?: string | null;
    codigoICMS?: number | null;
}

type ConfiguracoesSistemaApiDto = ConfiguracoesSistemaDto & {
    Id?: number;
    Nome?: string;
    Descricao?: string;
    Logging?: boolean | null;
    Lgpd?: boolean | null;
    BrasaoOficial?: boolean | null;
    Manutencao?: boolean | null;
    ManutencaoDescricao?: string;
    ManutencaoDataInicio?: string | null;
    ManutencaoDataFim?: string | null;
    CodigoICMS?: number | null;
};

const ENDPOINT = 'BFFProxy/configuracoesdosistema';

type BrasaoOficialListener = (brasaoOficial: boolean) => void;

const brasaoOficialListeners = new Set<BrasaoOficialListener>();
let currentBrasaoOficial = false;
let inflightConfigRequest: Promise<ConfiguracoesSistemaDto | null> | null = null;

function mapConfig(config: ConfiguracoesSistemaApiDto): ConfiguracoesSistemaDto {
    return {
        id: config.id ?? config.Id ?? 0,
        nome: config.nome ?? config.Nome,
        descricao: config.descricao ?? config.Descricao,
        logging: config.logging ?? config.Logging ?? false,
        lgpd: config.lgpd ?? config.Lgpd,
        brasaoOficial: config.brasaoOficial ?? config.BrasaoOficial ?? false,
        manutencao: config.manutencao ?? config.Manutencao,
        manutencaoDescricao: config.manutencaoDescricao ?? config.ManutencaoDescricao,
        manutencaoDataInicio: config.manutencaoDataInicio ?? config.ManutencaoDataInicio,
        manutencaoDataFim: config.manutencaoDataFim ?? config.ManutencaoDataFim,
        codigoICMS: config.codigoICMS ?? config.CodigoICMS,
    };
}

function emitBrasaoOficial(brasaoOficial: boolean): void {
    currentBrasaoOficial = brasaoOficial;
    brasaoOficialListeners.forEach((listener) => listener(currentBrasaoOficial));
}

export function subscribeBrasaoOficial(listener: BrasaoOficialListener): () => void {
    listener(currentBrasaoOficial);
    brasaoOficialListeners.add(listener);
    return () => brasaoOficialListeners.delete(listener);
}

export function getBrasaoOficialSnapshot(): boolean {
    return currentBrasaoOficial;
}

export async function obterConfiguracoesSistema(): Promise<ConfiguracoesSistemaDto | null> {
    if (inflightConfigRequest) {
        return inflightConfigRequest;
    }

    inflightConfigRequest = (async () => {
        try {
            const config = await HttpCotinService.Get<ConfiguracoesSistemaApiDto>(ENDPOINT);
            if (!config) {
                return null;
            }
            const mapped = mapConfig(config);
            emitBrasaoOficial(Boolean(mapped.brasaoOficial));
            return mapped;
        } catch {
            return null;
        }
    })().finally(() => {
        inflightConfigRequest = null;
    });

    return inflightConfigRequest;
}

export async function atualizarLogging(
    ativarLogging: boolean
): Promise<{ success: boolean; errors: string[] }> {
    try {
        const configAtual = await obterConfiguracoesSistema();
        if (!configAtual) {
            return { success: false, errors: ['Configuração do sistema não encontrada.'] };
        }

        if (Boolean(configAtual.logging) === ativarLogging) {
            return { success: true, errors: [] };
        }

        await HttpCotinService.Patch<boolean>(`${ENDPOINT}/Logging`, { logging: ativarLogging });
        return { success: true, errors: [] };
    } catch {
        return { success: false, errors: ['Falha ao atualizar status do logging.'] };
    }
}

export async function atualizarBrasaoOficial(
    ativarBrasaoOficial: boolean
): Promise<{ success: boolean; errors: string[] }> {
    try {
        const configAtual = await obterConfiguracoesSistema();
        if (!configAtual) {
            return { success: false, errors: ['Configuração do sistema não encontrada.'] };
        }

        if (Boolean(configAtual.brasaoOficial) === ativarBrasaoOficial) {
            emitBrasaoOficial(ativarBrasaoOficial);
            return { success: true, errors: [] };
        }

        await HttpCotinService.Patch<boolean>(`${ENDPOINT}/BrasaoOficial`, {
            brasaoOficial: ativarBrasaoOficial,
        });
        emitBrasaoOficial(ativarBrasaoOficial);
        return { success: true, errors: [] };
    } catch {
        return { success: false, errors: ['Falha ao atualizar status do brasão oficial.'] };
    }
}

export async function atualizarLgpd(ativarLgpd: boolean): Promise<{ success: boolean; errors: string[] }> {
    try {
        const configAtual = await obterConfiguracoesSistema();
        if (!configAtual) {
            return { success: false, errors: ['Configuração do sistema não encontrada.'] };
        }

        if (Boolean(configAtual.lgpd) === ativarLgpd) {
            return { success: true, errors: [] };
        }

        await HttpCotinService.Put<boolean>(ENDPOINT, {
            id: configAtual.id,
            nome: configAtual.nome ?? '',
            descricao: configAtual.descricao ?? '',
            logging: configAtual.logging ?? null,
            lgpd: ativarLgpd,
            brasaoOficial: configAtual.brasaoOficial ?? null,
            manutencao: configAtual.manutencao ?? null,
            manutencaoDescricao: configAtual.manutencaoDescricao ?? '',
            manutencaoDataInicio: configAtual.manutencaoDataInicio ?? null,
            manutencaoDataFim: configAtual.manutencaoDataFim ?? null,
            codigoICMS: configAtual.codigoICMS ?? null,
        });

        return { success: true, errors: [] };
    } catch {
        return { success: false, errors: ['Falha ao atualizar status LGPD.'] };
    }
}
